import React, {Fragment, useState, useEffect} from 'react';
import _ from 'lodash';
import {useParams} from 'react-router-dom';
import {Button, Icon, Colors, Intent, Card, Slider, Tag} from '@blueprintjs/core';
import {Breadcrumbs, Loading} from 'worksource-ui';
import {useQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import mixpanel from 'mixpanel-browser';

import {useUIActions} from 'utils/ui-context';
import {showGQLError} from 'utils/toaster';
import HeaderBar from 'components/header-bar';
import withMainLayout from 'layouts/main/view';
import AdditionalFeatureDialog from 'dialogs/AdditionalFeatureDialog';

const FETCH_BILLING_SCHEME = gql`
  query FetchBillingScheme($clientId: ID!) {
    clientBillingScheme(clientId: $clientId) {
      userRatesCents,
    }
    clientSubscription(clientId: $clientId) {
      seats,
      freeSeats,
    },
  }
`;

const ClientsPricingPage = () => {
  ////
  // Hooks
  //
  const params = useParams();
  const {setDialog} = useUIActions();
  const [userCount, setUserCount] = useState(75);
  const {loading, data} = useQuery(FETCH_BILLING_SCHEME, {
    variables: {clientId: params.clientId},
    onError: error => showGQLError({error}),
  });

  useEffect(() => {
    if (data && data.clientSubscription) {
      setUserCount(data.clientSubscription.seats - data.clientSubscription.freeSeats);
    }
  }, [(data || {}).clientSubscription]);

  useEffect(() => {
    mixpanel.track('Open client pricing page');
  }, []);

  ////
  // Events
  //
  const showRequestDialog = ({feature}) => {
    mixpanel.track('Open feature information dialog', {feature});
    setDialog(() => (
      <AdditionalFeatureDialog
        clientId={params.clientId}
        feature={feature}
      />
    ));
  };

  ////
  // Render
  //
  if (loading || !data) {
    return (
      <Loading />
    );
  } else if (!data.clientBillingScheme) {
    return (
      <p>
        No billing data found for this client.
      </p>
    );
  }

  const {userRatesCents: billingScheme} = data.clientBillingScheme;

  // Sort the tiers from highest to lowest
  const sorted = Object.entries(billingScheme)
    .map(([lower, upper]) => {
      return [parseInt(lower, 10), parseInt(upper, 10)];
    })
    .sort(([aUserCount], [bUserCount]) => {
      if (aUserCount > bUserCount) return -1;
      if (aUserCount < bUserCount) return 1;
      return 0;
    });

  const seatMap = _
    .range(1, userCount + 1)
    .map((seatNo) => {
      const [, tierPrice] = sorted.find(([lowerBound]) => seatNo >= lowerBound);
      return {seatNo, tierPrice};
    });
  const totalCost = _.sum(seatMap.map(({tierPrice}) => tierPrice));

  const [, tiers] = sorted.reduce(([remainingUsers, results], [lowerBound, currentTierPrice]) => {
    const countInTier = seatMap
      .map(({tierPrice}) => tierPrice)
      .filter(tierPrice => tierPrice === currentTierPrice)
      .length;

    const tier = {
      userCount: countInTier,
      cost: currentTierPrice,
      lowerBound,
    };
    return [
      remainingUsers - countInTier,
      [...results, tier],
    ];
  }, [userCount, []]);

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            current
            href={`/clients/${params.clientId}/plans`}
            icon="dollar"
            text="Plans & Pricing"
          />
        </Breadcrumbs>
      </HeaderBar>
      <div className="content-container">
        <div className="layout--full">
          <Card className="clients-pricing--card">
            <h1>Pricing Calculator</h1>
            <p>
              WorkSource's tiered pricing scheme allows organizations to reduce
              their per-seat costs as their team grows. Use the calculator below
              to get an estimate of your monthly costs for any number of seats.
            </p>
            <div className="clients-pricing--slider">
              <div className="user-count">
                <div className="metric-value">{userCount}</div>
                <div className="metric-title">Seats</div>
              </div>
              <Slider
                min={0}
                max={500}
                stepSize={1}
                labelStepSize={50}
                onChange={val => setUserCount(val)}
                value={userCount}
              />
            </div>
            <h2>Total cost</h2>
            <div className="clients-pricing--tiers">
              <div className="cost">
                <div className="total">
                  {`$${totalCost / 100}`}<span className="month-label">/mo</span>
                </div>
                {userCount !== 0 && (
                  <div className="avg">
                    Avg{' '}
                    <span className="avg-cost">{`$${(Math.round(totalCost / userCount) / 100).toFixed(2)}`}</span>
                    /seat/mo
                  </div>
                )}
              </div>
              <div className="equals">=</div>
              {tiers
                .reverse()
                .filter(({userCount: count}) => count > 0)
                .map(({userCount: count, cost, lowerBound}, i) => {
                  const upperBound = (tiers[i + 1] || {}).lowerBound - 1;
                  const bound = (() => {
                    if (upperBound) {
                      return `${lowerBound}-${upperBound}`;
                    }
                    return `${lowerBound}+`;
                  })();

                  return (
                    <Fragment key={lowerBound}>
                      <Tag large minimal>
                        Seats {bound}<br />
                        {`${count} x $${cost / 100}/mo`}
                      </Tag>
                      <Icon icon="plus" />
                    </Fragment>
                  );
                })
              }
            </div>
          </Card>

          <Card>
            <div className="clients-pricing--marketing-container">
              <h1>Features and Benefits</h1>
              <div className="clients-pricing--feature">
                <Icon icon="time" iconSize={48} color={Colors.GRAY1} />
                <h3>Anytime, anywhere access</h3>
                <p>
                  Our cloud-based portal provides immediate access to marketing
                  assets, allowing your sales team to quickly and easily access,
                  download, print or email. They can also favorite their most
                  useful tool and easily share files with prospects and
                  customers.
                </p>
              </div>
              <div className="clients-pricing--feature">
                <Icon icon="layout-auto" iconSize={48} color={Colors.GRAY1} />
                <h3>Manage and distribute content</h3>
                <p>
                  Updates and changes to assets are quickly managed through an
                  easy to use Content Management System (CMS).  Change
                  information once, and it's updated in every occurrence in the
                  CMS. Content is instantly available to your team via mobile
                  apps and web portal.
                </p>
              </div>
              <div className="clients-pricing--feature">
                <Icon icon="chart" iconSize={48} color={Colors.GRAY1} />
                <h3>Analytics that actually matter</h3>
                <p>
                  Find out what content works and what doesn't. What's being
                  shared, and more importantly, opened. Optimize content for your
                  team.
                </p>
              </div>
              <div className="clients-pricing--feature">
                <Icon icon="take-action" iconSize={48} color={Colors.GRAY1} />
                <h3>Keep compliant</h3>
                <p>
                  Keep all your content compliant and up-to-date by ensuring that
                  all materials in the CMS have been through the appropriate
                  regulatory channels.
                </p>
              </div>
              <div className="clients-pricing--feature">
                <Icon icon="edit" iconSize={48} color={Colors.GRAY1} />
                <h3>Annotate Content</h3>
                <p>
                  Compose and distribute notes and descriptions for pages,
                  sections and files to give your team more insights into
                  content, best practices, learnings and how to best communicate
                  the information you rely on to make sales.
                </p>
              </div>
              <div className="clients-pricing--feature">
                <Icon icon="offline" iconSize={48} color={Colors.GRAY1} />
                <h3>Empower your teams</h3>
                <p>
                  Improve productivity and efficiency with a one-stop shop for
                  your sales team, partners and distributors to access what they
                  need, when they need it.
                </p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="clients-pricing--additionals-container">
              <h1>Additional Options</h1>
              <div className="clients-pricing--option">
                <Icon icon="office" iconSize={56} />
                <div className="clients-pricing--option-content">
                  <h2>Whitelabeled Apps</h2>
                  <p>
                    Leverage the power of your brand with customized versions
                    of our web platform and mobile apps (both iOS and Android).
                    This package includes all setup and maintenance; just
                    provide us with your brand's assets and we'll take care of
                    the rest.
                  </p>
                  <Button
                    intent={Intent.PRIMARY}
                    onClick={() => showRequestDialog({feature: 'Whitelabeled apps'})}
                  >
                    Learn more
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout()(ClientsPricingPage);
